import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Star, ChevronLeft, ChevronRight } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const IMAGES = [
  {
    src: "/product-two-bottles.webp",
    label: "Two Bottles",
    alt: "Evove Skin Longevity Triple Serum bottle",
  },
  {
    src: "/serum-3circles.webp",
    label: "Three Serums",
    alt: "Three Evove serum formulations",
  },
  {
    src: "/product-hand.webp",
    label: "In Hand",
    alt: "Evove skincare bottle in hand",
  },
];

const CARD_BG =
  "radial-gradient(120% 100% at 50% 30%, #ffffff 0%, #f3f0ea 100%)";

const ITEMS = [
  {
    id: "technology",
    title: "Exclusive Technology",
    body: "Powered by our Patent Pending <strong>Tri-Delivery\u2122 Technology</strong>, this serum targets 3 different layers of the skin to reach the root cause of skin ageing.",
  },
  {
    id: "ingredients",
    title: "Active Ingredients",
    serums: [
      {
        name: "Serum 1: Barrier Repair & Ageing Defense",
        tag: "White",
        color: "#ECEAF6",
        border: "rgba(27,23,53,0.22)",
        list: [
          "5% Probiotic Ferment",
          "1% 5-Ceramide Complex",
          "1% Squalane",
          "1% Ectoin",
        ],
      },
      {
        name: "Serum 2: Cell Regeneration & Firmness",
        tag: "Hued Gel",
        color: "#EFA6C8",
        border: "transparent",
        list: [
          "4% ExoDuo\u2122 (Exosomes: Resveratrol & Cica)",
          "3% Dual Peptide Complex",
          "1,000 ppm Copper Peptide (GHK-Cu)",
        ],
      },
      {
        name: "Serum 3: Dark Spots & Radiance",
        tag: "Yellow",
        color: "#EFC65B",
        border: "transparent",
        list: [
          "2% Dual Encapsulated: Vit-C, Phytic Acid, Niacinamide, Azeloglicine, Bisabolol",
          "2% N-Acetyl Glucosamine",
          "2% Tranexamic Acid",
        ],
      },
    ],
  },
  {
    id: "formulated-for",
    title: "Formulated For",
    body: "Breakout-prone, sensitive skin, all skin types, daily use (AM/PM).",
  },
  {
    id: "formulated-without",
    title: "Formulated Without",
    list: ["Artificial Colors", "Fragrance", "Parabens", "Phthalates", "Silicone"],
  },
  {
    id: "backed-by",
    title: "Backed By",
    body: "Clinical testing + dermatologist recommendations.",
  },
  {
    id: "feels-like",
    title: "Feels Like",
    body: "A cushiony, silky texture that glides effortlessly, leaving skin hydrated, plump & dewy with a velvety finish.",
  },
];

function AccordionBody({ item }) {
  if (item.serums) {
    return (
      <div className="pb-7 pr-4 flex flex-col gap-7" data-testid={`accordion-content-${item.id}`}>
        {item.serums.map((s) => (
          <div key={s.name}>
            <div className="flex items-center gap-3">
              <span
                className="shrink-0 rounded-full"
                style={{
                  width: 12,
                  height: 12,
                  background: s.color,
                  border: `1px solid ${s.border}`,
                }}
              />
              <span
                className="text-[0.8rem] font-medium tracking-tight"
                style={{ color: "var(--paper)" }}
              >
                {s.name}{" "}
                <span style={{ color: "var(--paper-faint)" }}>({s.tag})</span>
              </span>
            </div>
            <ul className="mt-2 pl-[24px] flex flex-col gap-1">
              {s.list.map((li) => (
                <li
                  key={li}
                  className="text-[0.9rem] leading-relaxed"
                  style={{ color: "var(--paper-dim)" }}
                >
                  {li}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="pb-7 pr-8" data-testid={`accordion-content-${item.id}`}>
      {item.body && (
        <p
          className="text-[0.95rem] leading-relaxed max-w-xl"
          style={{ color: "var(--paper-dim)" }}
          dangerouslySetInnerHTML={{ __html: item.body }}
        />
      )}
      {item.list && (
        <ul className="flex flex-wrap gap-x-6 gap-y-3">
          {item.list.map((li) => (
            <li
              key={li}
              className="flex items-center gap-2 text-[0.95rem]"
              style={{ color: "var(--paper-dim)" }}
            >
              <span
                className="inline-block w-1 h-1 rounded-full"
                style={{ background: "var(--accent)" }}
              />
              {li}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ProductDetails() {
  const [active, setActive] = useState(0);

  // Preload all carousel images once on mount so switching is instant.
  // On mobile, preload the lighter "-m" variants; on desktop, the originals.
  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    IMAGES.forEach((im) => {
      const img = new Image();
      img.src = isMobile ? im.src.replace(".webp", "-m.webp") : im.src;
    });
  }, []);

  const touchX = useRef(0);
  const onTouchStart = (e) => {
    touchX.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40)
      setActive((a) =>
        dx < 0
          ? (a + 1) % IMAGES.length
          : (a - 1 + IMAGES.length) % IMAGES.length,
      );
  };

  return (
    <section
      className="relative w-full px-6 md:px-16 py-16 md:py-24"
      style={{ background: "var(--cream)" }}
      data-testid="product-section"
    >
      <div className="mx-auto max-w-7xl">
        <div className="frame flex items-center justify-between px-5 py-3">
          <span className="text-[0.58rem] track-sm uppercase" style={{ color: "var(--paper-dim)" }}>
            02 / Our 1st SKU
          </span>
          <span className="text-[0.58rem] track-sm uppercase" style={{ color: "var(--paper-faint)" }}>
            Skin Longevity Triple Serum
          </span>
        </div>

        {/* Heading + rating */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease }}
          className="mt-10 md:mt-14"
        >
          <h2 className="headline text-4xl sm:text-5xl lg:text-6xl">
            Skin Longevity Triple Serum
          </h2>
          <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="product-rating">
            <span className="flex gap-1" style={{ color: "var(--accent)" }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
              ))}
            </span>
            <span className="text-[0.62rem] track-sm uppercase" style={{ color: "var(--paper-dim)" }}>
              100% Recommended &middot; Loved by 120+ early testers
            </span>
          </div>
        </motion.div>

        {/* Images + details */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-start">
          {/* image viewer */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease }}
            className="lg:sticky lg:top-24"
            data-testid="product-image-viewer"
          >
            <div
              className="relative w-full aspect-[4/5] rounded-sm overflow-hidden frame"
              style={{ background: CARD_BG }}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              <picture key={IMAGES[active].src}>
                <source
                  media="(max-width: 767px)"
                  srcSet={IMAGES[active].src.replace(".webp", "-m.webp")}
                  type="image/webp"
                />
                <img
                  src={IMAGES[active].src}
                  alt={IMAGES[active].alt}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-contain p-6 fade-in"
                  data-testid="product-main-image"
                />
              </picture>
              <button
                onClick={() => setActive((active - 1 + IMAGES.length) % IMAGES.length)}
                data-testid="product-prev"
                aria-label="Previous image"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full grid place-items-center transition-transform hover:-translate-x-0.5"
                style={{ background: "#ffffff", border: "1px solid var(--line)", boxShadow: "0 4px 14px rgba(27,23,53,0.12)" }}
              >
                <ChevronLeft size={18} style={{ color: "var(--paper)" }} />
              </button>
              <button
                onClick={() => setActive((active + 1) % IMAGES.length)}
                data-testid="product-next"
                aria-label="Next image"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full grid place-items-center transition-transform hover:translate-x-0.5"
                style={{ background: "#ffffff", border: "1px solid var(--line)", boxShadow: "0 4px 14px rgba(27,23,53,0.12)" }}
              >
                <ChevronRight size={18} style={{ color: "var(--paper)" }} />
              </button>
            </div>
            <div className="mt-4 flex items-center gap-2">
              {IMAGES.map((im, i) => (
                <button
                  key={im.src}
                  onClick={() => setActive(i)}
                  data-testid={`product-dot-${i}`}
                  aria-label={im.label}
                  className="h-[6px] rounded-full transition-all duration-300"
                  style={{
                    width: active === i ? 22 : 6,
                    background: active === i ? "var(--paper)" : "var(--line)",
                  }}
                />
              ))}
            </div>
          </motion.div>

          {/* what it does + accordion */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease }}
            >
              <p
                className="text-base leading-relaxed max-w-xl"
                style={{ color: "var(--paper-dim)" }}
              >
                A powerful triple-dose serum that targets barrier repair, skin
                regeneration, wrinkles, and dark spots for visibly smoother,
                firmer, more even-toned skin. Powered by{" "}
                <strong style={{ color: "var(--paper)" }}>
                  Tri-Delivery&trade; Technology
                </strong>
                , a synergistic multi-pathway system designed to address
                multiple pathways of skin ageing across different skin layers.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: 0.1, ease }}
              className="mt-10"
            >
              {/*
                Native <details>/<summary> — every accordion's body content
                is ALWAYS rendered in the initial DOM (crawlable by Google
                and every LLM/agent even before any user interaction).
                <details open> collapses only the visual state, not the DOM.
              */}
              <div data-testid="product-accordion">
                {ITEMS.map((item, i) => (
                  <details
                    key={item.id}
                    open={i === 0}
                    className="evove-accordion group"
                    style={{ borderTop: "1px solid var(--line)" }}
                    data-testid={`accordion-item-${item.id}`}
                  >
                    <summary
                      className="flex items-center justify-between gap-4 py-6 text-left outline-none cursor-pointer select-none list-none"
                      data-testid={`accordion-trigger-${item.id}`}
                    >
                      <span
                        className="text-[0.8rem] md:text-[0.86rem] track-sm uppercase font-bold"
                        style={{ color: "var(--paper)" }}
                      >
                        {item.title}
                      </span>
                      <Plus
                        size={18}
                        strokeWidth={1.5}
                        className="accordion-icon shrink-0 transition-transform duration-500"
                        style={{ color: "var(--paper-dim)" }}
                      />
                    </summary>
                    <div className="overflow-hidden">
                      <AccordionBody item={item} />
                    </div>
                  </details>
                ))}
                <div style={{ borderBottom: "1px solid var(--line)" }} />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
